import {
  Component,
  DestroyRef,
  input,
  output,
  Renderer2,
  ChangeDetectionStrategy,
  computed,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, convertToParamMap } from '@angular/router';
import { APP } from 'src/app/config/app';
import { deserializeSuggestionsFilters, ISuggestionsFilters, resolveSuggestionsSort } from 'src/app/filters';
import { ISort } from 'src/app/models';
import {
  CommunicationUserType,
  FilterField,
  SuggestionPriority,
  SuggestionSortType,
  SuggestionStatus,
  SuggestionVisibility,
} from 'src/app/models/enums';
import { ISuggestion } from 'src/app/models/suggestion';
import { LiteralService, StorageService, UtilsService } from 'src/app/services';
import { checkboxFieldSource } from 'src/app/shared/filter-panel/filterOptionSources';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { ISuggestionState } from 'src/app/store/states/suggestions';
import { images } from 'src/images';
import {
  suggestionPriorityIcon,
  suggestionStatusIcon,
  suggestionTypeIcon,
  suggestionUserResolutionBadgeClass,
  suggestionVisibilityIcon,
  suggestionWithoutSuggesterLiteral,
} from 'src/app/utils/suggestionTickets';

@Component({
  selector: 'app-suggestions-table',
  templateUrl: './suggestions-table.component.html',
  styleUrl: './suggestions-table.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: {
    role: 'table',
    '[attr.aria-label]': "literalService.get('communications.types.suggestions', true)",
  },
})
export class SuggestionsTableComponent {
  readonly suggestions = input<ISuggestionState>();
  readonly userType = input<CommunicationUserType>();
  readonly selectedSuggestionId = input<number>();
  readonly selectSuggestion = output<any>();
  readonly changePriority = output<any>();
  readonly changeStatus = output<any>();
  readonly sortChange = output<ISort<SuggestionSortType>>();
  readonly filterChange = output<Partial<ISuggestionsFilters>>();

  private readonly queryParamMap = toSignal(this.activatedRoute.queryParamMap, {
    initialValue: this.activatedRoute.snapshot.queryParamMap,
  });
  private readonly storedSort = signal<ISort<SuggestionSortType> | null>(null);

  readonly sort = computed(() => {
    const storedSort = this.storedSort();
    return resolveSuggestionsSort(this.queryParamMap(), storedSort?.type, storedSort?.direction);
  });
  readonly ariaSortByType = computed(() =>
    this.utilsService.ariaSortByType(Object.values(SuggestionSortType), this.sort()),
  );
  readonly filters = computed(() => deserializeSuggestionsFilters(this.queryParamMap()));

  readonly typeFilterSource = checkboxFieldSource(this.literalService, FilterField.SUGGESTION_TYPES, () =>
    this.userType(),
  );
  readonly priorityFilterSource = checkboxFieldSource(this.literalService, FilterField.SUGGESTION_PRIORITIES);
  readonly statusFilterSource = checkboxFieldSource(this.literalService, FilterField.SUGGESTION_STATUS);
  readonly visibilityFilterSource = checkboxFieldSource(this.literalService, FilterField.SUGGESTION_VISIBILITIES);
  readonly userResolutionFilterSource = checkboxFieldSource(
    this.literalService,
    FilterField.SUGGESTION_USER_RESOLUTIONS,
  );

  public images = images;
  public CommunicationUserType = CommunicationUserType;
  public SuggestionStatus = SuggestionStatus;
  public SuggestionPriorities = SuggestionPriority;
  public SuggestionVisibility = SuggestionVisibility;
  readonly SuggestionSortType = SuggestionSortType;
  readonly priorityAndStatusColumnFiltersVisible = APP.SUGGESTIONS_PRIORITY_STATUS_COLUMN_FILTERS_VISIBLE;
  public TooltipPosition = TooltipPosition;
  public suggestionVisibilityIcon = suggestionVisibilityIcon;
  public suggestionPriorityIcon = suggestionPriorityIcon;
  public suggestionStatusIcon = suggestionStatusIcon;
  public suggestionTypeIcon = suggestionTypeIcon;
  public suggestionUserResolutionBadgeClass = suggestionUserResolutionBadgeClass;

  public showPriorityOptions: number | null = null;
  public showStatusOptions: number | null = null;

  constructor(
    public literalService: LiteralService,
    public utilsService: UtilsService,
    private storageService: StorageService,
    private activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    destroyRef: DestroyRef,
  ) {
    this.loadStoredSort();
    const removeWindowClickListener = renderer.listen('window', 'click', (event: Event) => {
      let modal = document.getElementById('suggestionsContainer')!;
      if (this.showPriorityOptions) {
        modal = document.getElementById('prioritiesContainer')!;
      } else if (this.showStatusOptions) {
        modal = document.getElementById('statusesContainer')!;
      }
      const close = utilsService.closeCustomModals(event, modal, modal);
      if (close) {
        if (this.showPriorityOptions) this.showPriorityOptions = null;
        if (this.showStatusOptions) this.showStatusOptions = null;
      }
    });
    destroyRef.onDestroy(removeWindowClickListener);
  }

  private async loadStoredSort() {
    const [storedSortType, storedSortDirection] = await Promise.all([
      this.storageService.getSuggestionsSortType(),
      this.storageService.getSuggestionsSortDirection(),
    ]);
    this.storedSort.set(resolveSuggestionsSort(convertToParamMap({}), storedSortType, storedSortDirection));
  }

  sortBy(sortType: SuggestionSortType) {
    const nextSort = this.utilsService.nextSort(this.sort(), sortType, APP.SUGGESTIONS_DEFAULT_DIRECTIONS);
    this.storedSort.set(nextSort);
    this.sortChange.emit(nextSort);
  }

  async showSuggestionData(id: number, edit?: boolean) {
    this.selectSuggestion.emit({ id, edit });
  }

  openSuggestion(event: MouseEvent, id: number) {
    if (event.ctrlKey || event.metaKey || event.shiftKey) return;
    event.preventDefault();
    this.showSuggestionData(id);
  }

  togglePriorityModal(event: any, id: number) {
    event.stopImmediatePropagation();
    event.preventDefault();
    if (this.showPriorityOptions === id) this.showPriorityOptions = null;
    else {
      if (this.showStatusOptions) this.showStatusOptions = null;
      this.showPriorityOptions = id;
    }
  }

  updatePriority(id: number, value: string) {
    this.changePriority.emit({ id, value });
    this.showPriorityOptions = null;
  }

  toggleStatusModal(event: any, id: number) {
    event.stopImmediatePropagation();
    event.preventDefault();
    if (this.showStatusOptions === id) this.showStatusOptions = null;
    else {
      if (this.showPriorityOptions) this.showPriorityOptions = null;
      this.showStatusOptions = id;
    }
  }

  updateStatus(id: number, value: string) {
    this.changeStatus.emit({ id, value });
    this.showStatusOptions = null;
  }

  getUserSuggesterName(suggestion: ISuggestion): string {
    if (suggestion.userSuggester?.id) {
      return `${suggestion.userSuggester.firstName} ${suggestion.userSuggester.lastName}`;
    } else {
      return this.literalService.get(suggestionWithoutSuggesterLiteral(suggestion), true);
    }
  }

  visibilityTooltip(suggestion: ISuggestion): string {
    const visibility = suggestion.visibility ?? 'PRIVATE';
    return `<strong>${this.literalService.get(`suggestions.visibilities.${visibility}`, true)}</strong></br>${this.literalService.get(`suggestions.visibilityTooltips.${visibility}`, true)}`;
  }
}
